import { useEffect, useRef, useState } from "react";

import { Fees } from "../../../../api/api-fees";
import {
  EMPTY_FEE_REGIONS,
  EMPTY_FEE_TYPES,
  type FeeRegion,
  type FeeType,
} from "../../app-main/selectors/fees.selectors";

const OK_STATUS_CODE = 200;

type RegionalFeesResponse = {
  fees?: FeeType[];
  regions?: FeeRegion[];
  statusCode?: number;
};

/** What came back, and the country it describes. */
type LoadedFees = {
  country: string;
  feeTypes: FeeType[];
  regions: FeeRegion[];
};

type RegionalTaxTypes = {
  feeTypes: FeeType[];
  isLoading: boolean;
  regions: FeeRegion[];
};

const NOTHING_LOADED: LoadedFees = {
  country: "",
  feeTypes: EMPTY_FEE_TYPES,
  regions: EMPTY_FEE_REGIONS,
};

/** The taxes charged in a country, for whichever country the form has selected. */
const useRegionalTaxTypes = ({
  accessToken,
  country,
}: {
  accessToken: string;
  country?: string;
}): RegionalTaxTypes => {
  const [loaded, setLoaded] = useState<LoadedFees>(NOTHING_LOADED);

  // Names the country each response belongs to, so a slow one for a country that
  // has since been changed is dropped rather than overwriting a newer answer.
  const pendingCountry = useRef<string | undefined>(undefined);

  useEffect(() => {
    pendingCountry.current = country;

    if (!country) {
      setLoaded(NOTHING_LOADED);

      return undefined;
    }

    Fees.fetchRegionalFees(
      accessToken,
      { country },
      (response: RegionalFeesResponse) => {
        if (country !== pendingCountry.current) {
          return;
        }

        const isOk = OK_STATUS_CODE === response?.statusCode;

        setLoaded({
          country,
          feeTypes: (isOk && response?.fees) || EMPTY_FEE_TYPES,
          regions: (isOk && response?.regions) || EMPTY_FEE_REGIONS,
        });
      },
    );

    // The request cannot be cancelled, so unmounting clears the country it was
    // for instead — the guard above then drops whatever comes back.
    return () => {
      pendingCountry.current = undefined;
    };
  }, [accessToken, country]);

  // Derived from which country the held data describes, rather than flagged from
  // inside the effect: the effect only runs after the render that picked the
  // country, so a flag set there would leave that render — the first one, and
  // every one that follows a change of country — holding another country's fees
  // while reporting itself loaded. Downstream that reads as "this region levies
  // no such tax", which is a falsehood the guest sees before it corrects itself.
  const isLoaded = (country ?? "") === loaded.country;

  return {
    feeTypes: isLoaded ? loaded.feeTypes : EMPTY_FEE_TYPES,
    isLoading: !isLoaded,
    regions: isLoaded ? loaded.regions : EMPTY_FEE_REGIONS,
  };
};

export { useRegionalTaxTypes };
